/**
 * UbicazioniTab — Elenco ubicazioni magazzino + generatore etichette QR.
 * L'admin sceglie una o più ubicazioni e stampa un PDF A4 con QR
 * (payload `UBIC:<nome>`) da attaccare direttamente sugli scaffali.
 * La scansione dei QR nella modalità "Scansione barcode" mostrerà tutti i
 * materiali stoccati in quella ubicazione.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { MapPin, Printer, Package, CheckSquare, Square } from "lucide-react";
import api from "@/api";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

export default function UbicazioniTab({ canEdit = true }) {
  const [selected, setSelected] = React.useState(() => new Set());

  const { data: ubicazioni = [], isLoading } = useQuery({
    queryKey: ["magazzino-ubicazioni"],
    queryFn: () => api.get("/magazzino/ubicazioni").then((r) => r.data),
  });

  const toggle = (name) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  const toggleAll = () => {
    if (selected.size === ubicazioni.length) setSelected(new Set());
    else setSelected(new Set(ubicazioni.map((u) => u.ubicazione)));
  };

  const stampaSelezionate = () => {
    if (selected.size === 0) {
      toast.error("Seleziona almeno un'ubicazione");
      return;
    }
    const codes = Array.from(selected).join("|");
    const url = `${backendUrl}/api/magazzino/ubicazioni/etichette.pdf?codes=${encodeURIComponent(codes)}`;
    window.open(url, "_blank");
  };

  const stampaTutte = () => {
    if (ubicazioni.length === 0) {
      toast.error("Nessuna ubicazione configurata");
      return;
    }
    const url = `${backendUrl}/api/magazzino/ubicazioni/etichette.pdf`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4" data-testid="ubicazioni-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="overline text-cyan-700">Ubicazioni</div>
          <h2 className="font-display text-xl font-black">Etichette scaffale QR</h2>
          <p className="text-slate-600 text-sm mt-1">
            Genera etichette QR da attaccare sugli scaffali. Scansionandole con la pistola vedrai
            immediatamente tutti i materiali stoccati in quella ubicazione.
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button
              onClick={stampaSelezionate}
              disabled={selected.size === 0}
              data-testid="btn-stampa-selezionate"
              className="inline-flex items-center gap-2 px-3 py-2 bg-cyan-700 hover:bg-cyan-800 disabled:opacity-50 text-white text-sm font-bold rounded-sm"
            >
              <Printer className="w-4 h-4" />
              Stampa selezionate ({selected.size})
            </button>
            <button
              onClick={stampaTutte}
              disabled={ubicazioni.length === 0}
              data-testid="btn-stampa-tutte"
              className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-slate-800 text-sm font-bold rounded-sm"
            >
              <Printer className="w-4 h-4" />
              Stampa tutte
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-400">Caricamento…</div>
        ) : ubicazioni.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <div className="font-bold text-slate-700">Nessuna ubicazione configurata</div>
            <div className="text-xs mt-1">
              Assegna il campo <b>Ubicazione</b> nei materiali del magazzino
              (es. "CORSIA A · SCAFFALE 3") per poter stampare le etichette QR.
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="p-3 w-10">
                  <button
                    onClick={toggleAll}
                    data-testid="btn-toggle-all-ubic"
                    className="p-1 hover:bg-slate-200 rounded-sm"
                    title="Seleziona/deseleziona tutte"
                  >
                    {selected.size === ubicazioni.length ? (
                      <CheckSquare className="w-4 h-4 text-cyan-700" />
                    ) : (
                      <Square className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                </th>
                <th className="text-left p-3">Ubicazione</th>
                <th className="text-right p-3">Materiali</th>
                <th className="text-right p-3">Qta tot</th>
                <th className="text-right p-3">QR payload</th>
              </tr>
            </thead>
            <tbody>
              {ubicazioni.map((u) => {
                const isSel = selected.has(u.ubicazione);
                return (
                  <tr
                    key={u.ubicazione}
                    onClick={() => toggle(u.ubicazione)}
                    data-testid={`ubic-row-${u.ubicazione}`}
                    className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${
                      isSel ? "bg-cyan-50" : ""
                    }`}
                  >
                    <td className="p-3">
                      {isSel ? (
                        <CheckSquare className="w-4 h-4 text-cyan-700" />
                      ) : (
                        <Square className="w-4 h-4 text-slate-400" />
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-cyan-600 shrink-0" />
                        <span className="font-bold text-slate-900">{u.ubicazione}</span>
                      </div>
                    </td>
                    <td className="p-3 text-right font-mono">
                      <Package className="w-3 h-3 inline text-slate-400 mr-1" />
                      {u.n_materiali}
                    </td>
                    <td className="p-3 text-right font-mono text-slate-600">{u.quantita_totale}</td>
                    <td className="p-3 text-right font-mono text-[11px] text-slate-500">
                      UBIC:{u.ubicazione}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-cyan-50 border border-cyan-200 rounded-sm p-3 text-xs text-cyan-900">
        <b>Layout etichette:</b> 24 etichette per pagina A4 (3 colonne × 8 righe).
        Ogni etichetta include un QR con payload <code className="bg-white px-1 rounded">UBIC:&lt;nome&gt;</code>,
        il nome dell'ubicazione a caratteri grandi e il conteggio dei materiali. Basta ritagliare e
        attaccare sullo scaffale.
      </div>
    </div>
  );
}
